/**
 * The strict parser for the `clarify` SSE payload (contract C8).
 *
 * Everything the card renders comes through here, so the rules are about what
 * can be SHOWN and ANSWERED: an option needs an id and a label; a smart-rec
 * option also needs `filter.need_id`, because picking it replays that need as
 * a `fetch_need` (an option without one could be displayed but never
 * answered — R2's original failure); a question needs text and at least one
 * option; a round needs an id, a known kind and at least one question. The
 * pre-round shape is retired and parses to `null`.
 */
import type {
  ClarifyKind,
  ClarifyOptionFilter,
  ClarifyRoundEvent,
  ClarifyRoundOption,
  ClarifyRoundQuestion,
} from "./types";

const KINDS: readonly ClarifyKind[] = ["clarify", "smart_rec_need", "smart_rec_facet", "quote"];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

/** The two smart-rec kinds: one question, answered by a `fetch_need`. */
export function isSmartRecKind(kind: ClarifyKind): boolean {
  return kind === "smart_rec_need" || kind === "smart_rec_facet";
}

function parseFilter(raw: unknown): ClarifyOptionFilter | null {
  if (!isRecord(raw)) return null;
  const needId = raw["need_id"];
  if (!isNonEmptyString(needId)) return null;
  const filter: ClarifyOptionFilter = { need_id: needId };
  const facetField = raw["facet_field"];
  const facetValue = raw["facet_value"];
  // Only keys that are really there: an R1 filter stays `{need_id}`, so it
  // never spreads empty facet keys into a request body.
  if (isNonEmptyString(facetField)) filter.facet_field = facetField;
  if (isNonEmptyString(facetValue)) filter.facet_value = facetValue;
  return filter;
}

function parseOption(raw: unknown, smartRec: boolean): ClarifyRoundOption | null {
  if (!isRecord(raw)) return null;
  const id = raw["id"];
  const label = raw["label"];
  if (!isNonEmptyString(id) || !isNonEmptyString(label)) return null;
  const filter = parseFilter(raw["filter"]);
  if (smartRec && filter === null) return null;
  const desc = raw["desc"];
  return {
    id,
    label,
    desc: isNonEmptyString(desc) ? desc : null,
    rec: raw["rec"] === true,
    filter,
  };
}

function parseQuestion(raw: unknown, smartRec: boolean): ClarifyRoundQuestion | null {
  if (!isRecord(raw)) return null;
  const id = raw["id"];
  const text = raw["text"];
  const optionsRaw = raw["options"];
  if (!isNonEmptyString(id) || !isNonEmptyString(text) || !Array.isArray(optionsRaw)) {
    return null;
  }
  const options = optionsRaw
    .map((option) => parseOption(option, smartRec))
    .filter((option): option is ClarifyRoundOption => option !== null);
  if (options.length === 0) return null;
  const header = raw["header"];
  return {
    id,
    header: typeof header === "string" ? header : "",
    text,
    multi: raw["multi"] === true,
    allow_custom: raw["allow_custom"] === true,
    allow_dont_know: raw["allow_dont_know"] === true,
    options,
  };
}

/** Parse one `clarify` frame's data. `null` = nothing the card could show. */
export function parseClarifyRound(data: Record<string, unknown>): ClarifyRoundEvent | null {
  const roundId = data["round_id"];
  const kindRaw = data["kind"];
  const questionsRaw = data["questions"];
  if (!isNonEmptyString(roundId) || !Array.isArray(questionsRaw)) return null;
  const kind = KINDS.find((candidate) => candidate === kindRaw);
  if (kind === undefined) return null;
  const smartRec = isSmartRecKind(kind);
  const questions = questionsRaw
    .map((question) => parseQuestion(question, smartRec))
    .filter((question): question is ClarifyRoundQuestion => question !== null);
  if (questions.length === 0) return null;
  const customMaxChars = data["custom_max_chars"];
  return {
    round_id: roundId,
    kind,
    questions,
    // The schema default is true (C3); only an explicit false removes the escape.
    allow_skip: data["allow_skip"] !== false,
    // The live «Свой ответ» cap (index amendment A3) — only when it is usable.
    ...(typeof customMaxChars === "number" && Number.isInteger(customMaxChars) && customMaxChars > 0
      ? { custom_max_chars: customMaxChars }
      : {}),
  };
}
